import * as l from "@cerulea/lexicon";
import type { PostView } from "../queries.ts";
import { Like, Repost } from "../structs.ts";
import { RpcClient, type RpcTransport, XrpcError } from "./api.ts";
import { listAll, type RecordRef, recordRpcs, storedMatch } from "./pds.ts";
import { canWriteCollection } from "./permissions.ts";
import { createTid, rkeyOf } from "./repo.ts";

const value = l.union({ [Like.id]: Like.record, [Repost.id]: Repost.record });
const interactions = recordRpcs(l.enumValues(Like.id, Repost.id), value);

type Kind = "like" | "repost";
type State = { readonly like: boolean; readonly repost: boolean };

export class PostInteractions extends EventTarget {
  readonly #client: RpcClient;
  readonly #records = {
    like: new Map<string, RecordRef[]>(),
    repost: new Map<string, RecordRef[]>(),
  };
  readonly #pending = new Set<string>();
  #loaded: Promise<void> | undefined;

  constructor(
    readonly viewer: `did:${string}`,
    readonly scopes: ReadonlySet<string>,
    transport: RpcTransport,
  ) {
    super();
    this.#client = new RpcClient(transport);
  }

  async get(subject: string): Promise<State> {
    await this.#ensureLoaded();
    return {
      like: !!this.#records.like.get(subject)?.length,
      repost: !!this.#records.repost.get(subject)?.length,
    };
  }

  async toggle(kind: Kind, post: PostView): Promise<boolean> {
    const schema = kind === "like" ? Like : Repost;
    const key = `${kind}:${post.uri}`;
    if (this.#pending.has(key)) throw new Error("This interaction is already being updated");
    this.#pending.add(key);
    try {
      await this.#ensureLoaded();
      const records = this.#records[kind].get(post.uri) ?? [];
      const active = records.length > 0;
      if (!canWriteCollection(this.scopes, schema.id, active ? "delete" : "create")) {
        throw new Error(`Authorize permission to ${active ? "remove" : "create"} ${kind}s first`);
      }
      if (active) {
        for (const record of records) {
          await this.#client.call(interactions.delete, {
            parameters: {},
            input: {
              repo: this.viewer,
              collection: schema.id,
              rkey: rkeyOf(record.uri),
              swapRecord: record.cid,
            },
          }).catch((error) => {
            if (!(error instanceof XrpcError) || error.error !== "RecordNotFound") throw error;
          });
        }
        this.#records[kind].delete(post.uri);
      } else {
        const address = { repo: this.viewer, collection: schema.id, rkey: createTid() };
        const record = {
          $type: schema.id,
          subject: { uri: post.uri, cid: post.cid },
          createdAt: new Date().toISOString(),
        } as l.Infer<typeof value>;
        let result: RecordRef;
        try {
          result = await this.#client.call(interactions.create, {
            parameters: {},
            input: { ...address, record },
          });
        } catch (error) {
          const stored = await storedMatch(this.#client, interactions, address, record);
          if (!stored) throw error;
          result = stored;
        }
        this.#records[kind].set(post.uri, [result]);
      }
      const next = !active;
      this.dispatchEvent(
        new CustomEvent("change", { detail: { subject: post.uri, kind, active: next } }),
      );
      return next;
    } finally {
      this.#pending.delete(key);
    }
  }

  async #ensureLoaded(): Promise<void> {
    const loading = this.#loaded ??= this.#load();
    try {
      await loading;
    } catch (error) {
      if (this.#loaded === loading) {
        this.#loaded = undefined;
        this.#records.like.clear();
        this.#records.repost.clear();
      }
      throw error;
    }
  }

  async #load(): Promise<void> {
    await Promise.all([this.#loadCollection(Like.id), this.#loadCollection(Repost.id)]);
  }

  async #loadCollection(target: typeof Like.id | typeof Repost.id): Promise<void> {
    const kind = target === Like.id ? "like" : "repost";
    for (const record of await listAll(this.#client, interactions, this.viewer, target)) {
      const existing = this.#records[kind].get(record.value.subject.uri) ?? [];
      existing.push({ uri: record.uri, cid: record.cid });
      this.#records[kind].set(record.value.subject.uri, existing);
    }
  }
}
